// src/components/QueryPage.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useData } from '../context/DataContext';
import styles from './QueryPage.module.css';
import MultiSelectDropdown from './MultiSelectDropDown';
import Spinner from './Spinner';
import { useNavigate, useLocation } from 'react-router-dom';
import SortableTable from '../utils/SortableTable';
import { countryList } from './../utils/CountryList';

// --- NEW HELPER FUNCTION (from DiscoveryPage) ---
const getTierClassName = (tier) => {
  switch (tier?.toLowerCase()) {
    case 'prime':
    case 'excellent':
    case 'excellent fit':
    case 'great':
      return styles.tierPrime;
    case 'promising':
    case 'good':
    case 'high fit':
    case 'high':
      return styles.tierPromising;
    case 'moderate fit':
    case 'moderate':
    case 'acceptable':
      return styles.tierAcceptable;
    case 'low fit':
    case 'low':
    case 'mid-risk':
      return styles.tierMidRisk; // Note: Matches the CSS class .midRisk
    case 'high-risk':
    case 'poor':
    case 'poor fit':
    case 'not acceptable':
      return styles.tierHighRisk; // Note: Matches the CSS class .highRisk
    case 'Incomplete':
    case 'limited Data':
      return styles.tierInsufficient;
    case 'inactive':
      return styles.tierInactive;
    case 'error':
      return styles.tierError;
    default:
      return styles.tierUnknown;
  }
};

const QueryPage = () => {
  const {
    queryResults,
    setQueryResults,
    lastQuery,
    setLastQuery,
    queryTableSorting, // <-- ADDED: Get sorting state from context
    setQueryTableSorting, // <-- ADDED: Get sorting dispatcher from context
    activeinfluencerId,
    setActiveinfluencerId,
    isQueryMode,
    setQueryMode,
    tasks,
    runSingleQuery,
    runMultipleQuery,
    userProfile, // ADDED: To check the user's role
    allUsers, // ADDED: To populate the dropdown
    fetchUsers, // ADDED: To fetch the list of users
  } = useData();

  const navigate = useNavigate();
  const location = useLocation();

  // Local states for the 4 single query input fields
  const [nameQuery, setNameQuery] = useState('');
  const [nicknameQuery, setNicknameQuery] = useState('');
  const [emailQuery, setEmailQuery] = useState('');
  const [promoCodeQuery, setPromoCodeQuery] = useState('');

  // Local states for single select multiple query fields
  const [dateFromQuery, setDateFromQuery] = useState('');
  const [dateToQuery, setDateToQuery] = useState('');
  const [dateDiscoveryFromQuery, setDateDiscoveryFromQuery] = useState('');
  const [dateDiscoveryToQuery, setDateDiscoveryToQuery] = useState('');
  const [taskLabel, setTaskLabel] = useState('');
  const [taskType, setTaskType] = useState('');

  // --- Local states for multi select multiple query filters
  const [statusQuery, setStatusQuery] = useState(['ALL']);
  const [mediaQuery, setMediaQuery] = useState(['ALL']);
  const [countryQuery, setCountryQuery] = useState(['ALL']);
  const [healthTierQuery, setHealthTierQuery] = useState(['ALL']);
  const [focusTierQuery, setFocusTierQuery] = useState(['ALL']);
  const [fitTierQuery, setFitTierQuery] = useState(['ALL']);
  const [qualityTierQuery, setQualityTierQuery] = useState(['ALL']);
  const [userQuery, setUserQuery] = useState(['ALL']);

  // Other local states
  const [loading, setLoading] = useState(false);
  const [spinnerMessage, setSpinnerMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(''); // Local error message

  useEffect(() => {
    if (userProfile?.role === 'Admin') {
      fetchUsers();
    }
  }, [userProfile, fetchUsers]);

  useEffect(() => {
    // Auto-configure the Query Form to find the fresh leads!
    if (location.state?.fromAgentDiscovery) {
      window.history.replaceState({}, document.title); // Prevent looping on refresh

      setStatusQuery(['NEW']);

      const today = new Date().toISOString().split('T')[0];
      setDateDiscoveryFromQuery(today);
      setDateDiscoveryToQuery(today);
    }
  }, [location.state]);

  useEffect(() => {
    // We only run this logic if we have a list of results in query mode.
    if (isQueryMode && queryResults?.length > 0) {
      // Check if the currently active ID is actually present in the results list.
      const isActiveIdInResults = queryResults.some(
        (item) => item.influencerId === activeinfluencerId,
      );

      // If the active ID is NOT in the results (it could be null, or from a stale query),
      // then we need to select a new, valid one to prevent a broken state.
      if (!isActiveIdInResults) {
        console.log(
          `[QueryPage] Active ID (${activeinfluencerId}) is not in the current query results. Defaulting to the first item.`,
        );
        setActiveinfluencerId(queryResults[0].influencerId);
      }
      // If isActiveIdInResults is true, we do nothing, preserving the user's last selection.
    }
  }, [isQueryMode, queryResults, activeinfluencerId, setActiveinfluencerId]);

  // This effect synchronizes the form inputs with the last run query from the context.
  // --- COMBINED HYDRATION AND RE-FETCH HOOK ---
  useEffect(() => {
    // Helper function to set form fields based on a query object
    const hydrateForm = (query) => {
      const { type, params } = query;

      // Reset all fields to default to ensure a clean slate
      setNameQuery('');
      setNicknameQuery('');
      setEmailQuery('');
      setPromoCodeQuery('');
      setStatusQuery(['ALL']);
      setMediaQuery(['ALL']);
      setCountryQuery(['ALL']);
      setHealthTierQuery(['ALL']);
      setFocusTierQuery(['ALL']);
      setFitTierQuery(['ALL']);
      setQualityTierQuery(['ALL']);
      setUserQuery(['ALL']);
      setDateFromQuery('');
      setDateToQuery('');
      setDateDiscoveryFromQuery('');
      setDateDiscoveryToQuery('');
      setTaskLabel('');
      setTaskType('');

      if (type === 'single' && params) {
        if (params.type === 'name') setNameQuery(params.value || '');
        if (params.type === 'nickname') setNicknameQuery(params.value || '');
        if (params.type === 'email') setEmailQuery(params.value || '');
        if (params.type === 'promoCode') setPromoCodeQuery(params.value || '');
      } else if (type === 'multiple' && params) {
        // Ensure we handle if saved query was old string format or new array format
        const ensureArray = (val, defaultVal) =>
          Array.isArray(val) ? val : [val || defaultVal];

        setStatusQuery(ensureArray(params.statusQuery, 'ALL'));
        setMediaQuery(ensureArray(params.mediaQuery, 'ALL'));
        setCountryQuery(ensureArray(params.countryQuery, 'ALL'));
        setHealthTierQuery(ensureArray(params.healthTierQuery, 'ALL'));
        setFocusTierQuery(ensureArray(params.focusTierQuery, 'ALL'));
        setFitTierQuery(ensureArray(params.fitTierQuery, 'ALL'));
        setQualityTierQuery(ensureArray(params.qualityTierQuery, 'ALL'));
        setUserQuery(ensureArray(params.userQuery, 'ALL'));
        setDateFromQuery(params.dateFromQuery || '');
        setDateToQuery(params.dateToQuery || '');
        setDateDiscoveryFromQuery(params.dateDiscoveryFromQuery || '');
        setDateDiscoveryToQuery(params.dateDiscoveryToQuery || '');
        if (params.taskType) {
          const matchingTask = tasks.find((t) => t.type === params.taskType);
          if (matchingTask) {
            setTaskLabel(matchingTask.nameMenu);
            setTaskType(matchingTask.type);
          }
        }
      }
    };

    // Asynchronous function to re-run the last query
    const restoreAndFetchQuery = async () => {
      hydrateForm(lastQuery); // First, set the form inputs visually

      console.log('[QueryPage] Re-running last query to populate results...');
      setLoading(true);
      setSpinnerMessage('Restoring last query...');
      setErrorMessage('');

      try {
        const { type, params } = lastQuery;
        let results = [];
        if (type === 'single') {
          results = await runSingleQuery(params);
        } else if (type === 'multiple') {
          results = await runMultipleQuery(params);
        }

        if (Array.isArray(results)) {
          setQueryResults(results); // This populates the table
        } else {
          setErrorMessage('Re-running query failed to return valid results.');
        }
      } catch (err) {
        console.error('Error re-running last query:', err);
        setErrorMessage(
          err.message || 'Could not restore the last query results.',
        );
        setQueryResults([]);
        setLastQuery(null);
        setQueryMode(false);
      } finally {
        setLoading(false);
        setSpinnerMessage('');
      }
    };

    // --- DECISION LOGIC ---
    if (isQueryMode && lastQuery) {
      // If results are not populated in the context, we MUST re-fetch them.
      if (!queryResults || queryResults.length === 0) {
        restoreAndFetchQuery();
      }
      // If results ARE already populated (from localStorage), just make the form match.
      else {
        console.log(
          '[QueryPage] Results exist in context. Hydrating form only.',
        );
        hydrateForm(lastQuery);
      }
    }
  }, [isQueryMode, lastQuery, tasks]); // Dependencies for restoring state

  // Define columns for the SortableTable in the format @tanstack/react-table expects
  const columns = useMemo(() => {
    const baseColumns = [
      { header: 'Channel Name', accessorKey: 'name', size: 250 },
      { header: 'Nickname', accessorKey: 'nickname', size: 200 },
      { header: 'Status', accessorKey: 'status', size: 150 },
      { header: 'Country', accessorKey: 'country', size: 150 },
      { header: 'Media', accessorKey: 'media', size: 120 },
      { header: 'Contact', accessorKey: 'email', size: 220 },
      { header: 'Promo Code', accessorKey: 'promoCode', size: 170 },
      {
        header: 'Discovered',
        accessorKey: 'discovered',
        cell: (info) => {
          const date = info.getValue();
          return date
            ? new Date(date).toLocaleString([], {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'N/A';
        },
        size: 170,
      },
      {
        header: 'LastContact',
        accessorKey: 'dateLastContact',
        cell: (info) => {
          const date = info.getValue();
          return date
            ? new Date(date).toLocaleString([], {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'N/A';
        },
        size: 180,
      },
      {
        header: 'Ch.Focus',
        accessorKey: 'contentFocusTier',
        cell: ({ row }) => {
          const tier = row.original.contentFocusTier || 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)} ${styles.nonClickable}`}
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Ch.Health',
        accessorKey: 'healthTier',
        cell: ({ row }) => {
          const tier = row.original.healthTier || 'N/A';
          return (
            <div
              className={`${styles.healthTierCell} ${getTierClassName(tier)} ${styles.nonClickable}`}
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Content.Fit',
        accessorKey: 'marketFitTier',
        cell: ({ row }) => {
          const tier = row.original.marketFitTier || 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)} ${styles.nonClickable}`}
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Cnt.Quality',
        accessorKey: 'contentQualityTier',
        cell: ({ row }) => {
          const tier = row.original.contentQualityTier || 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)} ${styles.nonClickable}`}
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Subscribers',
        accessorKey: 'subscribers',
        cell: (info) => {
          const value = info.getValue();
          return typeof value === 'number' ? value.toLocaleString() : '0';
        },
        size: 180,
      },
      {
        header: 'Views',
        accessorKey: 'viewCount',
        cell: (info) => {
          const value = info.getValue();
          return typeof value === 'number' ? value.toLocaleString() : '0';
        },
        size: 130,
      },
    ];
    // --- MODIFIED: Conditionally add the "Owner" column ---
    if (userProfile?.role === 'Admin' && userQuery === 'ALL') {
      const ownerColumn = {
        header: 'Manager',
        accessorKey: 'ownerName', // This must match the key from the backend
        size: 180,
      };
      // Insert the owner column after the 'Channel Name' column
      baseColumns.splice(1, 0, ownerColumn);
    }
    return baseColumns;
  }, [userProfile, userQuery]);

  // Build a list of unique task nameMenus from tasks (for the dropdown)
  const taskLabelList = useMemo(() => {
    return Array.from(
      new Set(tasks.map((t) => t.nameMenu).filter((nm) => nm !== '')),
    );
  }, [tasks]);

  // When user picks a label, find the matching .type
  const handleTaskLabelChange = useCallback(
    (e) => {
      const label = e.target.value;
      setTaskLabel(label);
      const matchingTaskObj = tasks.find((t) => t.nameMenu === label);
      setTaskType(matchingTaskObj ? matchingTaskObj.type : '');
    },
    [tasks],
  );

  // Main query logic
  const handleRunQuery = useCallback(async () => {
    setErrorMessage('');
    setLoading(true);
    setSpinnerMessage('Please wait while running query ...');

    try {
      const filledFields = [
        nameQuery.trim(),
        nicknameQuery.trim(),
        emailQuery.trim(),
        promoCodeQuery.trim(),
      ].filter(Boolean);

      if (filledFields.length > 1) {
        setErrorMessage(
          "Only one of 'Name', 'Nickname', 'Email', or 'Promo Code' can be filled.",
        );
        return;
      }

      let results = [];
      let queryToStore = null; // This will hold the object we save to global state

      if (filledFields.length === 1) {
        // This is a Single Query
        const queryType = nameQuery.trim()
          ? 'name'
          : nicknameQuery.trim()
            ? 'nickname'
            : emailQuery.trim()
              ? 'email'
              : 'promoCode';
        const queryValue =
          nameQuery.trim() ||
          nicknameQuery.trim() ||
          emailQuery.trim() ||
          promoCodeQuery.trim();

        const queryParams = { type: queryType, value: queryValue };
        results = await runSingleQuery(queryParams);

        // Prepare the standardized object to store in state
        queryToStore = { type: 'single', params: queryParams };
      } else {
        // This is a Multiple Query
        const queryParams = {
          statusQuery,
          mediaQuery,
          dateFromQuery,
          dateToQuery,
          dateDiscoveryFromQuery,
          dateDiscoveryToQuery,
          taskType,
          countryQuery,
          healthTierQuery,
          focusTierQuery,
          fitTierQuery,
          qualityTierQuery,
          userQuery, // MODIFIED
        };
        results = await runMultipleQuery(queryParams);

        // Prepare the standardized object to store in state
        queryToStore = { type: 'multiple', params: queryParams };
      }

      if (!Array.isArray(results)) {
        // This handles cases where the API might return an error object instead of an array
        console.error('Query did not return an array:', results);
        setErrorMessage(
          'Query did not return expected results. Check console for details.',
        );
      }

      // Update the main results list in the global state
      setQueryResults(results);

      if (results.length > 0) {
        // If we have results, store the successful query for later refetching
        setLastQuery(queryToStore);

        // Set the first influencer as the active one and switch to query mode
        setActiveinfluencerId(results[0].influencerId);
        setQueryMode(true);
      } else {
        // If there are no results, clear the last query and reset state
        setLastQuery(null);
        setActiveinfluencerId(null);
        setQueryMode(false);
        setQueryResults([]);
        setErrorMessage('No results found. Please modify your query.');
      }
    } catch (err) {
      setErrorMessage(
        err.message ||
          'An error occurred while running the query. Please try again.',
      );
      // Ensure we clear the last query on a full failure as well
      setLastQuery(null);
    } finally {
      setLoading(false);
      setSpinnerMessage('');
    }
  }, [
    // All dependencies required for the function to work correctly
    runSingleQuery,
    runMultipleQuery,
    setQueryResults,
    setActiveinfluencerId,
    setQueryMode,
    setLastQuery,
    nameQuery,
    nicknameQuery,
    emailQuery,
    promoCodeQuery,
    statusQuery,
    mediaQuery,
    dateFromQuery,
    dateToQuery,
    dateDiscoveryFromQuery,
    dateDiscoveryToQuery,
    taskType,
    countryQuery,
    healthTierQuery,
    focusTierQuery,
    fitTierQuery,
    qualityTierQuery,
    userQuery, // MODIFIED
  ]);

  // Handler for setting the active influencer when a row is clicked
  const handleRowClick = useCallback(
    (influencer) => {
      setActiveinfluencerId(influencer.influencerId);
      navigate('/influencer');
    },
    [setActiveinfluencerId],
  );

  // Clear query results and inputs
  const toggleQueryMode = useCallback(() => {
    setQueryResults([]);
    setActiveinfluencerId(null);
    setQueryMode(false);
    setErrorMessage('');

    // Clear all input fields
    setNameQuery('');
    setNicknameQuery('');
    setEmailQuery('');
    setPromoCodeQuery('');
    setStatusQuery(['ALL']);
    setMediaQuery(['ALL']);
    setCountryQuery(['ALL']);
    setHealthTierQuery(['ALL']);
    setFocusTierQuery(['ALL']);
    setFitTierQuery(['ALL']);
    setQualityTierQuery(['ALL']);
    setUserQuery(['ALL']);
    setDateFromQuery('');
    setDateToQuery('');
    setDateDiscoveryFromQuery('');
    setDateDiscoveryToQuery('');

    // Clear the new task filter states
    setTaskLabel('');
    setTaskType('');
  }, [setQueryResults, setActiveinfluencerId, setQueryMode]);

  return (
    <div className={styles.queryPage}>
      {/* Spinner rendering */}
      {loading && <Spinner message={spinnerMessage} />}

      {/* — First Row: Name, Contact, Promo Code, Single Query Fields */}
      <div className={styles.queryRow}>
        <div className={styles.queryFieldsContainer}>
          <div className={styles.queryField}>
            <input
              className={styles.queryField}
              type="text"
              placeholder="Enter Channel's Name"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
            />
          </div>
          <div className={styles.queryField}>
            <input
              className={styles.queryField}
              type="text"
              placeholder="Enter Influencer's Nickname"
              value={nicknameQuery}
              onChange={(e) => setNicknameQuery(e.target.value)}
            />
          </div>
          <div className={styles.queryField}>
            <input
              className={styles.queryField}
              type="text"
              placeholder="Enter Influencer's Contact"
              value={emailQuery}
              onChange={(e) => setEmailQuery(e.target.value)}
            />
          </div>
          <div className={styles.queryField}>
            <input
              className={styles.queryField}
              type="text"
              placeholder="Enter Influencer's Promo Code"
              value={promoCodeQuery}
              onChange={(e) => setPromoCodeQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* — ROW 2: Status, Media, Country, and Tier Filters */}
      <div className={styles.queryRow}>
        <div className={styles.queryFieldsContainer}>
          {/* Admin User Filter */}
          {userProfile?.role === 'Admin' && (
            <div className={`${styles.queryField} ${styles.composite}`}>
              <MultiSelectDropdown
                label="All Users"
                defaultLabel="ALL"
                selectedValues={userQuery}
                onChange={setUserQuery}
                options={allUsers.map((u) => ({
                  label: u.userName,
                  value: u.userId,
                }))}
              />
            </div>
          )}

          {/* Status Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="All Status"
              defaultLabel="ALL"
              selectedValues={statusQuery}
              onChange={setStatusQuery}
              options={[
                { label: 'New Influencer', value: 'NEW' },
                { label: 'Sent', value: 'SENT' },
                { label: 'Accessing', value: 'ACCESSING' },
                { label: 'New Partner', value: 'NEW PARTNER' },
                { label: 'Partner', value: 'PARTNER' },
                { label: 'On Hold', value: 'ON HOLD' },
                { label: 'Refused', value: 'REFUSED' },
              ]}
            />
          </div>

          {/* Media Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="All Media"
              defaultLabel="ALL"
              selectedValues={mediaQuery}
              onChange={setMediaQuery}
              options={[
                { label: 'YouTube', value: 'YouTube' },
                { label: 'Instagram', value: 'Instagram' },
                { label: 'Facebook', value: 'Facebook' },
                { label: 'TikTok', value: 'TikTok' },
                { label: 'Website', value: 'Website' },
              ]}
            />
          </div>

          {/* Country Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="All Countries"
              defaultLabel="ALL"
              selectedValues={countryQuery}
              onChange={setCountryQuery}
              options={countryList.map((c) => ({
                label: c.label,
                value: c.label,
              }))}
            />
          </div>

          {/* Focus Tier Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="Any Ch. Focus"
              defaultLabel="ALL"
              selectedValues={focusTierQuery}
              onChange={setFocusTierQuery}
              options={[
                { label: 'Great', value: 'Great' },
                { label: 'Good', value: 'Good' },
                { label: 'Moderate', value: 'Moderate' },
                { label: 'Low', value: 'Low' },
                { label: 'Poor', value: 'Poor' },
              ]}
            />
          </div>

          {/* Health Tier Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="Any Ch. Health"
              defaultLabel="ALL"
              selectedValues={healthTierQuery}
              onChange={setHealthTierQuery}
              options={[
                { label: 'Prime', value: 'Prime' },
                { label: 'Promising', value: 'Promising' },
                { label: 'Acceptable', value: 'Acceptable' },
                { label: 'Mid-Risk', value: 'Mid-Risk' },
                { label: 'High-Risk', value: 'High-Risk' },
                { label: 'Inactive', value: 'Inactive' },
              ]}
            />
          </div>

          {/* Fit Tier Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="Any Content Fit"
              defaultLabel="ALL"
              selectedValues={fitTierQuery}
              onChange={setFitTierQuery}
              options={[
                { label: 'Excellent Fit', value: 'Excellent Fit' },
                { label: 'High Fit', value: 'High Fit' },
                { label: 'Moderate Fit', value: 'Moderate Fit' },
                { label: 'Low Fit', value: 'Low Fit' },
                { label: 'Poor Fit', value: 'Poor Fit' },
              ]}
            />
          </div>

          {/* Quality Tier Filter */}
          <div className={`${styles.queryField} ${styles.composite}`}>
            <MultiSelectDropdown
              label="Any Cnt Quality"
              defaultLabel="ALL"
              selectedValues={qualityTierQuery}
              onChange={setQualityTierQuery}
              options={[
                { label: 'Excellent', value: 'Excellent' },
                { label: 'Good', value: 'Good' },
                { label: 'Acceptable', value: 'Acceptable' },
                { label: 'Low', value: 'Low' },
                { label: 'Not Acceptable', value: 'Not Acceptable' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* — Third Row: Task Filter, Discovered On + Run/Clear buttons */}
      <div className={styles.queryRow}>
        <div className={styles.queryFieldsContainer}>
          <div>
            <div className={`${styles.queryField} ${styles.composite2}`}>
              <label htmlFor="DateFrom">Last Contact from: </label>
              <input
                id="DateFrom"
                className={styles.queryField}
                type="date"
                value={dateFromQuery}
                onChange={(e) => setDateFromQuery(e.target.value)}
              />
            </div>
          </div>
          <div>
            <div className={`${styles.queryField} ${styles.composite2}`}>
              <label htmlFor="DateTo">To:</label>
              <input
                id="DateTo"
                className={styles.queryField}
                type="date"
                value={dateToQuery}
                onChange={(e) => setDateToQuery(e.target.value)}
              />
            </div>
          </div>
          <div className={`${styles.queryField} ${styles.composite2}`}>
            <label htmlFor="discoveryDateFrom">Discovered from: </label>
            <input
              id="discoveryDateFrom"
              type="date"
              value={dateDiscoveryFromQuery}
              onChange={(e) => setDateDiscoveryFromQuery(e.target.value)}
            />
          </div>
          <div className={`${styles.queryField} ${styles.composite2}`}>
            <label htmlFor="discoveryDateTo">To: </label>
            <input
              id="discoveryDateTo"
              type="date"
              value={dateDiscoveryToQuery}
              onChange={(e) => setDateDiscoveryToQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.runQueryButton}>
          <button onClick={handleRunQuery}>Run Query</button>
          {isQueryMode && (
            <button
              className={styles.exitQueryButton}
              onClick={toggleQueryMode}
            >
              Clear Query
            </button>
          )}
        </div>
      </div>

      {/* — Local Error Message */}
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

      {/* — Results Table */}
      {isQueryMode && queryResults.length > 0 && (
        <div style={{ width: '95%', marginTop: '0.5rem', marginRight: '1rem' }}>
          <div className={styles.queryResultsHeader}>
            <span className={styles.queryIndexDisplay}>
              # of Influencers: {queryResults.length}
            </span>
          </div>
          <SortableTable
            data={queryResults}
            columns={columns}
            enableStickyHeader={true}
            stickyFirstColumn={true}
            activeRowId={activeinfluencerId}
            onRowClick={handleRowClick}
            sorting={queryTableSorting}
            onSortingChange={setQueryTableSorting}
          />
        </div>
      )}
    </div>
  );
};

export default QueryPage;
